import { useQuery, useMutation, useQueryClient } from "react-query";
import { lockRetryOptions } from "../utils/mutationRetry";
import { makeAuthedRequest } from "../utils/makeAuthedRequest";
import { parseApiError } from "../utils/apiError";
import { useContactId, useContactGate } from "./useContactId";
import { enhancedStockInfo } from "../utils/mockStockData";
import { computeStockStatus } from "../utils/stockStatus";
import { resolveImageUrl } from "../utils/resolveImageUrl";
import {
  getVariantColorKey,
  resolveVariantColorAndSize,
} from "../utils/variantParsing";

const MOCK_STOCK_FLAG_KEY = "wwgStoreUseMockStockData";

const SYSTEM_LIST_ORDER = [
  "Purchased Items",
  "Favorites",
  "Saved for Later",
  "Books Read",
];

const WWG_PRIORITY_ORDER = [
  "Purchased Items",
  "Favorites",
  "Saved for Later",
  "Books Read",
];
const WWG_PRIORITY_KEYS = WWG_PRIORITY_ORDER.reduce((acc, name, index) => {
  acc[toListKey(name)] = index;
  return acc;
}, {});

const SYSTEM_LIST_TYPES = new Set([
  "PURCHASED_ITEMS",
  "USER_FAVORITE",
  "SAVED_FOR_LATER",
  "BOOKS_READ",
]);

const SYSTEM_LIST_KEYS = SYSTEM_LIST_ORDER.reduce((acc, name, index) => {
  acc[toListKey(name)] = index;
  return acc;
}, {});

function toListKey(value = "") {
  return String(value).trim().toLowerCase();
}

function isSystemListByName(name) {
  return Object.prototype.hasOwnProperty.call(
    SYSTEM_LIST_KEYS,
    toListKey(name)
  );
}

function isSystemListByType(type) {
  return SYSTEM_LIST_TYPES.has(
    String(type ?? "")
      .trim()
      .toUpperCase()
  );
}

function normalizeList(list) {
  const listId = list?.listId ?? list?.setId ?? null;
  const systemByName = isSystemListByName(list?.name);
  const systemByType = isSystemListByType(list?.type);
  return {
    ...list,
    listId,
    setId: listId,
    type: list?.type ?? "GENERIC",
    isNew: list?.isNew ?? false,
    isModal: list?.isModal ?? false,
    isSystemList: Boolean(list?.isSystemList ?? (systemByName || systemByType)),
    isNavigable: Boolean(listId),
  };
}

function sortAndHydrateLists(lists) {
  const normalized = (Array.isArray(lists) ? lists : []).map(normalizeList);
  const userLists = normalized.sort((a, b) => {
    const aIsSystem = Boolean(a?.isSystemList);
    const bIsSystem = Boolean(b?.isSystemList);

    // Keep all WWG/system lists above custom lists.
    if (aIsSystem && !bIsSystem) return -1;
    if (!aIsSystem && bIsSystem) return 1;

    const aKey = toListKey(a?.name);
    const bKey = toListKey(b?.name);
    const aPriority = WWG_PRIORITY_KEYS[aKey];
    const bPriority = WWG_PRIORITY_KEYS[bKey];
    const aIsPriority = Number.isInteger(aPriority);
    const bIsPriority = Number.isInteger(bPriority);

    // For system lists, enforce explicit WWG order when available.
    if (aIsSystem && bIsSystem) {
      if (aIsPriority && bIsPriority) return aPriority - bPriority;
      if (aIsPriority) return -1;
      if (bIsPriority) return 1;
    }

    return String(a?.name ?? "").localeCompare(
      String(b?.name ?? ""),
      undefined,
      {
        sensitivity: "base",
      }
    );
  });
  return userLists;
}

function filterListsByContact(lists, contactId) {
  const contactKey = String(contactId ?? "");
  return (Array.isArray(lists) ? lists : []).filter(
    (list) => String(list?.contactId ?? "") === contactKey
  );
}

function getListId(list) {
  return list?.listId ?? list?.setId ?? null;
}

function shouldUseMockStockData() {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(MOCK_STOCK_FLAG_KEY) === "true";
}

function normalizeAttributeToken(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_");
}

function deriveAttributeFlags(attributes = []) {
  const tokens = new Set();

  (Array.isArray(attributes) ? attributes : []).forEach((attribute) => {
    tokens.add(normalizeAttributeToken(attribute?.type));
    tokens.add(normalizeAttributeToken(attribute?.code));
    tokens.add(normalizeAttributeToken(attribute?.label));
  });

  const has = (token) => tokens.has(token);

  return {
    is3rdParty:
      has("SOURCE_3RD_PARTY") ||
      has("3RD_PARTY") ||
      has("THIRD_PARTY") ||
      has("SOURCE_THIRD_PARTY"),
    isHot: has("HOT") || has("IS_HOT") || has("TAG_HOT"),
    isNew: has("NEW") || has("IS_NEW") || has("TAG_NEW") || has("NEW_RELEASE"),
  };
}

function toBooleanLike(value) {
  if (typeof value === "boolean") return value;
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  return ["true", "1", "yes", "y"].includes(normalized);
}

// Variant-level flags (is3rdParty/isHot/isTrending/isNew) mirror the product-level flags but
// are matched per (variationId, variantId) by the backend. Data-model-only for now: no
// StockItem/ColumnStockItem badge rendering reads these yet, but the fields are always present
// so the UI can opt in later without another backend round-trip.
function normalizeVariantFlags(variant) {
  return {
    is3rdParty: Boolean(toBooleanLike(variant?.is3rdParty)),
    isHot: Boolean(toBooleanLike(variant?.isHot)),
    isTrending: Boolean(toBooleanLike(variant?.isTrending)),
    isNew: Boolean(toBooleanLike(variant?.isNew)),
  };
}

function normalizeItemVariants(item) {
  const variants = Array.isArray(item?.variants) ? item.variants : null;
  if (!variants) return variants;
  return variants.map((variant) => ({
    ...variant,
    ...normalizeVariantFlags(variant),
  }));
}

// Derives the color/size pickers CartItem needs for CONFIGURABLE items
// straight from the list-items payload's `variants` array, so the saved-list
// row never has to fetch /stock-items just to render/edit variant choices.
function deriveVariantMetadata(item) {
  const variants = Array.isArray(item?.variants) ? item.variants : [];
  if (variants.length === 0) {
    return { detailColors: [], sizeOptions: [], variantMatrix: {} };
  }

  const colorImageMap = new Map();
  const sizeSellability = new Map();
  const variantMatrix = {};

  variants.forEach((variant) => {
    const { color, size } = resolveVariantColorAndSize(variant);
    const normalizedColor = String(color || "").trim();
    const normalizedSize = String(size || "").trim();

    const colorKey = getVariantColorKey(variant, normalizedColor);
    if (normalizedColor && !colorImageMap.has(colorKey)) {
      colorImageMap.set(colorKey, {
        label: normalizedColor,
        imageUrl: resolveImageUrl(variant) || resolveImageUrl(item) || null,
        swatchHex: variant?.swatchHex || null,
        swatchImage: variant?.swatchImage || null,
      });
    }

    if (normalizedSize) {
      const isSellable = variant?.isSellable !== false;
      const wasSellable = sizeSellability.get(normalizedSize);
      sizeSellability.set(
        normalizedSize,
        typeof wasSellable === "boolean"
          ? wasSellable || isSellable
          : isSellable
      );
    }

    if (normalizedColor || normalizedSize) {
      variantMatrix[`${normalizedColor}::${normalizedSize}`] = {
        variantId: variant?.variantId ?? null,
        variationId: variant?.variationId ?? null,
      };
    }
  });

  return {
    detailColors: Array.from(colorImageMap.values()).filter(
      (option) => option?.label
    ),
    sizeOptions: Array.from(sizeSellability.entries()).map(
      ([label, isSellable]) => ({
        label,
        isSoldOut: !isSellable,
      })
    ),
    variantMatrix,
  };
}

function deriveItemFlags(item, attributeFlags) {
  const sourceToken = normalizeAttributeToken(item?.source);
  const sourceLabelToken = normalizeAttributeToken(item?.sourceLabel);

  const is3rdPartyFromSource =
    sourceToken === "SOURCE_3RD_PARTY" ||
    sourceToken === "3RD_PARTY" ||
    sourceLabelToken === "3RD_PARTY" ||
    sourceLabelToken === "THIRD_PARTY";

  return {
    is3rdParty: Boolean(
      toBooleanLike(item?.is3rdParty) ||
        toBooleanLike(item?.isThirdParty) ||
        toBooleanLike(item?.thirdParty) ||
        attributeFlags.is3rdParty ||
        is3rdPartyFromSource
    ),
    isHot: Boolean(
      toBooleanLike(item?.isHot) ||
        toBooleanLike(item?.hot) ||
        attributeFlags.isHot
    ),
    isNew: Boolean(
      toBooleanLike(item?.isNew) ||
        toBooleanLike(item?.new) ||
        attributeFlags.isNew
    ),
  };
}

function buildInjectedMockListItem(sourceItem, listId) {
  const fallback = enhancedStockInfo.find(
    (item) => item?.itemType === "CONFIGURABLE"
  );
  if (!fallback) return sourceItem;

  return {
    ...sourceItem,
    stockId: fallback.stockId,
    productId: fallback.stockId,
    title: fallback.title,
    memberPrice: fallback.memberPrice,
    retailPrice: fallback.retailPrice,
    itemType: fallback.itemType,
    imageUrl: fallback.imageUrl,
    status: sourceItem?.status ?? "ACTIVE",
    isUnavailable: false,
    isTrending: false,
    description: fallback.description,
    sku: fallback.sku,
    isLiked: sourceItem?.isLiked ?? false,
    variationId: sourceItem?.variationId ?? null,
    variantId: sourceItem?.variantId ?? null,
    listProductId: sourceItem?.listProductId ?? `mock-${listId}`,
    __isInjectedMock: true,
  };
}

function buildInjectedShirtWithSizes(sourceItem, listId) {
  const shirtWithSizes = enhancedStockInfo.find(
    (item) =>
      item?.itemType === "CONFIGURABLE" &&
      Array.isArray(item?.sizeOptions) &&
      item.sizeOptions.length > 0
  );

  const fallback =
    shirtWithSizes ||
    enhancedStockInfo.find((item) => item?.itemType === "CONFIGURABLE");

  if (!fallback) return sourceItem;

  return {
    ...sourceItem,
    stockId: fallback.stockId,
    productId: fallback.stockId,
    title: fallback.title,
    memberPrice: fallback.memberPrice,
    retailPrice: fallback.retailPrice,
    itemType: fallback.itemType,
    imageUrl: fallback.imageUrl,
    status: sourceItem?.status ?? "ACTIVE",
    isUnavailable: false,
    isTrending: false,
    description: fallback.description,
    sku: fallback.sku,
    isLiked: sourceItem?.isLiked ?? false,
    variationId: sourceItem?.variationId ?? null,
    variantId: sourceItem?.variantId ?? null,
    listProductId: sourceItem?.listProductId ?? `mock-shirt-${listId}`,
    __isInjectedMock: true,
  };
}

function normalizeListItem(item, contactId) {
  const contactKey = String(contactId ?? "");
  let isLiked = item?.isLiked;
  if (typeof isLiked !== "boolean" && isLiked && typeof isLiked === "object") {
    isLiked = Boolean(isLiked[contactKey]);
  }

  const rawStockId =
    item?.stockId ?? item?.productId ?? item?.stockItemId ?? item?.id ?? null;
  const parsedStockId = Number(rawStockId);
  const stockId =
    rawStockId == null || rawStockId === ""
      ? null
      : Number.isNaN(parsedStockId)
      ? rawStockId
      : parsedStockId;

  const attributeFlags = deriveAttributeFlags(item?.attributes);
  const itemFlags = deriveItemFlags(item, attributeFlags);

  const variationId =
    item?.variationId ??
    item?.variationID ??
    item?.variation?.id ??
    item?.variation?.variationId ??
    null;

  const variantId =
    item?.variantId ??
    item?.variantID ??
    item?.variant?.id ??
    item?.variant?.variantId ??
    null;

  const variants = normalizeItemVariants(item);
  const variantMetadata = deriveVariantMetadata({ ...item, variants });

  return {
    ...item,
    stockId,
    variationId,
    variantId,
    variants,
    ...variantMetadata,
    imageUrl: resolveImageUrl(item) || item?.imageUrl,
    ...computeStockStatus(item),
    isLiked: Boolean(isLiked),
    is3rdParty: itemFlags.is3rdParty,
    isHot: itemFlags.isHot,
    isNew: itemFlags.isNew,
  };
}

function categoryRank(category) {
  const normalized = String(category || "")
    .trim()
    .toLowerCase();
  if (normalized === "literature") return 3;
  if (normalized === "books") return 2;
  if (!normalized) return 0;
  return 1;
}

function buildListItemIdentity(item) {
  const listProductId = item?.listProductId ?? item?.id ?? null;
  if (listProductId != null && listProductId !== "") {
    return `listProduct::${listProductId}`;
  }

  const stockId = item?.stockId ?? "";
  const productId = item?.productId ?? "";
  return `stockProduct::${stockId}::${productId}`;
}

function dedupeListItems(items = []) {
  const seen = new Map();

  items.forEach((item) => {
    const key = buildListItemIdentity(item);
    const existing = seen.get(key);

    if (!existing) {
      seen.set(key, item);
      return;
    }

    const existingRank = categoryRank(existing?.category);
    const incomingRank = categoryRank(item?.category);
    if (incomingRank > existingRank) {
      seen.set(key, item);
    }
  });

  return Array.from(seen.values());
}

function normalizeListItems(items, contactId) {
  if (!Array.isArray(items)) return [];
  const deduped = dedupeListItems(items);
  return deduped.map((item) => normalizeListItem(item, contactId));
}

export function useLists(options = {}) {
  const { enabled = true } = options;
  const contactId = useContactId();
  const parsedContactId = Number(contactId);
  const hasValidContactId =
    contactId != null && contactId !== "" && !Number.isNaN(parsedContactId);

  const query = useQuery(
    ["lists", contactId],
    async () => {
      const query = `?contactId=${encodeURIComponent(String(parsedContactId))}`;
      const res = await makeAuthedRequest(`/lists${query}`);
      if (!res.ok) throw await parseApiError(res, "Lists fetch failed");
      const lists = await res.json();
      return sortAndHydrateLists(filterListsByContact(lists, parsedContactId));
    },
    {
      enabled: enabled && hasValidContactId,
    }
  );

  return query;
}

export function useListItems(listId) {
  const useMockStockData = shouldUseMockStockData();
  const { contactId, gateOpen, gatePending } = useContactGate({
    bypass: useMockStockData,
  });

  const query = useQuery(
    // isLiked comes back as a per-contactId map from the backend (an IBO can
    // have multiple household contacts), so contactId is real cache-key data.
    // It resolves asynchronously, so we gate with enabled below instead of
    // firing before it resolves and normalizing against null.
    ["listItems", listId, contactId, useMockStockData],
    async () => {
      const res = await makeAuthedRequest(`/lists/${listId}/items`);
      if (!res.ok) throw await parseApiError(res, "List items fetch failed");
      const items = await res.json();

      const normalizedItems = normalizeListItems(items, contactId);

      if (!useMockStockData) {
        return normalizedItems;
      }

      if (normalizedItems.length > 0) {
        const firstInjected = buildInjectedMockListItem(
          normalizedItems[0],
          listId
        );
        const secondInjected = buildInjectedShirtWithSizes(
          normalizedItems[1] ?? {},
          listId
        );

        return [firstInjected, secondInjected, ...normalizedItems.slice(2)];
      }

      return [
        buildInjectedMockListItem({}, listId),
        buildInjectedShirtWithSizes({}, listId),
      ];
    },
    { enabled: !!listId && gateOpen }
  );

  // See useStockItems: fold isIdle into isLoading using gatePending verbatim
  // so it can never drift from the enabled gate above.
  return {
    ...query,
    isLoading: query.isLoading || (query.isIdle && !!listId && gatePending),
  };
}

export function useCreateList() {
  const contactId = useContactId();
  const parsedContactId = Number(contactId);
  const hasValidContactId =
    contactId != null && contactId !== "" && !Number.isNaN(parsedContactId);
  const queryClient = useQueryClient();
  return useMutation(
    async (payload) => {
      if (!hasValidContactId) {
        throw new Error("Create list failed: missing contactId");
      }

      const res = await makeAuthedRequest("/lists", {
        method: "POST",
        body: JSON.stringify({ ...payload, contactId: parsedContactId }),
      });
      if (!res.ok) throw await parseApiError(res, "Create list failed");
      return res.json();
    },
    {
      onMutate: async (payload) => {
        const queryKey = ["lists", contactId];
        await queryClient.cancelQueries(queryKey);
        const previousLists = queryClient.getQueryData(queryKey) ?? [];
        const tempId = `temp-${Date.now()}`;

        const optimisticList = normalizeList({
          listId: tempId,
          setId: tempId,
          name: payload?.name ?? "",
          type: "GENERIC",
          isNew: false,
          isModal: false,
          isSystemList: false,
        });

        queryClient.setQueryData(
          queryKey,
          sortAndHydrateLists([...previousLists, optimisticList])
        );

        return { previousLists, optimisticListId: optimisticList.listId };
      },
      onError: (_err, _payload, context) => {
        if (context?.previousLists) {
          queryClient.setQueryData(["lists", contactId], context.previousLists);
        }
      },
      onSuccess: (createdList, _payload, context) => {
        const normalizedCreated = {
          ...normalizeList(createdList),
          isNew: false,
          isModal: false,
        };

        queryClient.setQueryData(["lists", contactId], (current = []) =>
          sortAndHydrateLists(
            current.map((list) =>
              String(getListId(list)) === String(context?.optimisticListId)
                ? normalizedCreated
                : list
            )
          )
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries(["lists", contactId]);
      },
    }
  );
}

export function useDeleteList() {
  const queryClient = useQueryClient();
  return useMutation(
    async (listId) => {
      const res = await makeAuthedRequest(`/lists/${listId}`, {
        method: "DELETE",
      });
      if (!res.ok) throw await parseApiError(res, "Delete list failed");
    },
    {
      onMutate: async (listId) => {
        await queryClient.cancelQueries("lists");
        const previousLists = queryClient.getQueryData("lists") ?? [];

        queryClient.setQueryData("lists", (current = []) =>
          sortAndHydrateLists(
            current.filter((list) => String(getListId(list)) !== String(listId))
          )
        );

        return { previousLists };
      },
      onError: (_err, _listId, context) => {
        if (context?.previousLists) {
          queryClient.setQueryData("lists", context.previousLists);
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries("lists");
      },
    }
  );
}

export function useUpdateList() {
  const contactId = useContactId();
  const parsedContactId = Number(contactId);
  const hasValidContactId =
    contactId != null && contactId !== "" && !Number.isNaN(parsedContactId);
  const queryClient = useQueryClient();
  return useMutation(
    async ({ listId, name }) => {
      const payload = hasValidContactId
        ? { name, contactId: parsedContactId }
        : { name };

      const res = await makeAuthedRequest(`/list/${listId}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw await parseApiError(res, "Update list failed");
      return res.json();
    },
    {
      onMutate: async ({ listId, name }) => {
        await queryClient.cancelQueries("lists");
        const previousLists = queryClient.getQueryData("lists") ?? [];

        queryClient.setQueryData("lists", (current = []) =>
          sortAndHydrateLists(
            current.map((list) =>
              String(getListId(list)) === String(listId)
                ? { ...list, name }
                : list
            )
          )
        );

        return { previousLists };
      },
      onError: (_err, _payload, context) => {
        if (context?.previousLists) {
          queryClient.setQueryData("lists", context.previousLists);
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries("lists");
      },
    }
  );
}

export function useAddListItem() {
  const queryClient = useQueryClient();
  return useMutation(
    async ({
      listId,
      stockId,
      variationId = null,
      variantId = null,
      sortOrder = null,
    }) => {
      const res = await makeAuthedRequest(`/lists/${listId}/items`, {
        method: "POST",
        body: JSON.stringify({
          stockId,
          variationId,
          variantId,
          sortOrder,
        }),
      });
      if (!res.ok) throw await parseApiError(res, "Add list item failed");
      return res.json();
    },
    {
      onMutate: async ({
        listId,
        stockId,
        variationId = null,
        variantId = null,
        sortOrder = null,
        // Optional display metadata for the item already known by the
        // caller (e.g. a cart row being moved to Saved For Later). The
        // add-item response doesn't include pricing/title info, so without
        // this the optimistic entry shows $0.00 until the follow-up
        // listItems refetch resolves.
        title,
        memberPrice,
        retailPrice,
        imageUrl,
        image,
        itemType,
        author,
        color,
        size,
      }) => {
        await queryClient.cancelQueries(["listItems", listId]);

        const previousListItemQueries = queryClient.getQueriesData([
          "listItems",
          listId,
        ]);

        previousListItemQueries.forEach(([queryKey, currentData]) => {
          if (!Array.isArray(currentData)) return;

          const alreadyExists = currentData.some((item) => {
            const existingStockId =
              item?.stockId ?? item?.productId ?? item?.stockItemId;
            return String(existingStockId ?? "") === String(stockId ?? "");
          });

          if (alreadyExists) return;

          const optimisticItem = {
            stockId,
            productId: stockId,
            variationId,
            variantId,
            sortOrder,
            title,
            memberPrice,
            retailPrice,
            imageUrl,
            image,
            itemType,
            author,
            color,
            size,
            listProductId: `optimistic-${Date.now()}-${stockId}`,
          };

          queryClient.setQueryData(queryKey, [...currentData, optimisticItem]);
        });

        return { previousListItemQueries };
      },
      onError: (_err, _variables, context) => {
        context?.previousListItemQueries?.forEach(
          ([queryKey, previousData]) => {
            queryClient.setQueryData(queryKey, previousData);
          }
        );
      },
      onSuccess: (_, { listId }) => {
        queryClient.invalidateQueries(["listItems", listId]);
        // Adding to a list (e.g. Saved For Later) may cause the backend to
        // silently remove a matching cart row; keep the cart view in sync.
        queryClient.invalidateQueries("cart");
        // Stock queries carry each item's isInList membership, which the
        // add-to-list modal uses to grey out/checkmark lists the item is
        // already in. Without this, the modal shows stale membership until
        // a full page refresh re-fetches stock data.
        queryClient.invalidateQueries("stockItems");
        queryClient.invalidateQueries("stockItem");
      },
      onSettled: (_, __, { listId }) => {
        queryClient.invalidateQueries(["listItems", listId]);
      },
      ...lockRetryOptions,
    }
  );
}

export function useRemoveListItem() {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ listId, listProductId, stockId }) => {
      const itemId = listProductId ?? stockId;
      const res = await makeAuthedRequest(`/lists/${listId}/items/${itemId}`, {
        method: "DELETE",
      });
      if (!res.ok) throw await parseApiError(res, "Remove list item failed");
    },
    {
      onMutate: async ({ listId, listProductId, stockId }) => {
        await queryClient.cancelQueries(["listItems", listId]);

        const targetId = listProductId ?? stockId;
        const previousListItemQueries = queryClient.getQueriesData([
          "listItems",
          listId,
        ]);

        // Optimistically remove the row so deletion feels instant.
        previousListItemQueries.forEach(([queryKey, currentData]) => {
          if (!Array.isArray(currentData) || targetId == null) return;

          const nextData = currentData.filter((item) => {
            const itemId =
              item?.listProductId ??
              item?.stockId ??
              item?.productId ??
              item?.id;
            return String(itemId) !== String(targetId);
          });

          queryClient.setQueryData(queryKey, nextData);
        });

        return { previousListItemQueries };
      },
      onError: (_err, _variables, context) => {
        context?.previousListItemQueries?.forEach(
          ([queryKey, previousData]) => {
            queryClient.setQueryData(queryKey, previousData);
          }
        );
      },
      onSuccess: () => {
        // Cart rows also carry isInList membership now, so removing an item
        // from a list (e.g. Saved For Later, during move-to-cart) must
        // refresh the cart view too, not just stock queries.
        queryClient.invalidateQueries("cart");
        // Keep stock queries' isInList membership in sync after removal too.
        queryClient.invalidateQueries("stockItems");
        queryClient.invalidateQueries("stockItem");
      },
      onSettled: (_, __, { listId }) => {
        queryClient.invalidateQueries(["listItems", listId]);
      },
      ...lockRetryOptions,
    }
  );
}

export function useUpdateListItem() {
  const queryClient = useQueryClient();

  const applyOptimisticListItemUpdate = (items, listProductId, payload) => {
    if (!Array.isArray(items)) return items;

    return items.map((item) => {
      const itemId = item?.listProductId ?? item?.id ?? item?.stockId;
      if (String(itemId) !== String(listProductId)) {
        return item;
      }

      return {
        ...item,
        variationId:
          payload?.variationId !== undefined
            ? payload.variationId
            : item?.variationId,
        variantId:
          payload?.variantId !== undefined
            ? payload.variantId
            : item?.variantId,
        quantity:
          payload?.quantity !== undefined ? payload.quantity : item?.quantity,
        color: payload?.color !== undefined ? payload.color : item?.color,
        size: payload?.size !== undefined ? payload.size : item?.size,
      };
    });
  };

  return useMutation(
    async ({
      listId,
      listProductId,
      variationId = null,
      variantId = null,
      quantity = undefined,
    }) => {
      const requestBody = {};

      // Include variantId if it's not null (to preserve existing variant or set new one)
      if (variantId !== null && variantId !== undefined) {
        requestBody.variantId = variantId;
      }

      // Include quantity if provided
      if (quantity !== undefined && quantity !== null) {
        requestBody.quantity = quantity;
      }

      // Include variationId if provided (some backends might need this)
      if (variationId !== null && variationId !== undefined) {
        requestBody.variationId = variationId;
      }

      const res = await makeAuthedRequest(
        `/lists/${listId}/items/${listProductId}`,
        {
          method: "PUT",
          body: JSON.stringify(requestBody),
        }
      );
      if (!res.ok) {
        const error = await parseApiError(res, "Update list item failed");
        if (error.status === 409 && !error.code) {
          error.code = "LIST_ITEM_DUPLICATE";
        }
        if (error.status === 404 && !error.code) {
          error.code = "LIST_ITEM_NOT_FOUND";
        }
        throw error;
      }
      return res.json();
    },
    {
      onMutate: async ({ listId, listProductId, ...payload }) => {
        await queryClient.cancelQueries(["listItems", listId]);

        const previousListItemQueries = queryClient.getQueriesData([
          "listItems",
          listId,
        ]);

        previousListItemQueries.forEach(([queryKey, currentData]) => {
          const nextData = applyOptimisticListItemUpdate(
            currentData,
            listProductId,
            payload
          );
          queryClient.setQueryData(queryKey, nextData);
        });

        return { previousListItemQueries };
      },
      onError: (_err, _variables, context) => {
        context?.previousListItemQueries?.forEach(
          ([queryKey, previousData]) => {
            queryClient.setQueryData(queryKey, previousData);
          }
        );
      },
      onSuccess: (_, { listId }) => {
        queryClient.invalidateQueries(["listItems", listId]);
      },
      onSettled: (_, __, { listId }) => {
        queryClient.invalidateQueries(["listItems", listId]);
      },
      ...lockRetryOptions,
    }
  );
}
